import React, {useEffect,useState,useMemo,} from "react";

import UserTable from "./UserTable";
import UserFilters from "./UserFilters";
import UserModal from "./UserModal";
import ViewUserDrawer from "./ViewUserDrawer";
import PermissionDrawer from "./PermissionDrawer";
import ResetPasswordModal from "./ResetPasswordModal";
import Pagination from "../Components/Pagination";

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [hubs, setHubs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [statusFilter,setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [limit] = useState(1000);
  const [totalRecords,setTotalRecords] = useState(0);
  const pageSize = 5;
  const [showModal,setShowModal] = useState(false);
  const [showDrawer,setShowDrawer] = useState(false);
  const [showPermissionDrawer,setShowPermissionDrawer] = useState(false);
  const [showResetModal,setShowResetModal] = useState(false);
  const [selectedUser,setSelectedUser] = useState(null);
  const [permissions,setPermissions] = useState([]);

  useEffect(() => {
    fetchRoles();
    fetchHubs();
  }, []);

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [search, roleFilter, statusFilter]);

  useEffect(() => {
    setPage(1);
  }, [search, roleFilter, statusFilter]);

  const fetchUsers = async () => {
      try {
        setLoading(true);
        const query = new URLSearchParams({
            page: 1,
            limit,
            search,
            role: roleFilter,
            status: statusFilter
          });
        const response = await fetch(`${process.env.REACT_APP_FORE_APILINK}/user/get-all-users?${query}`);
        const result = await response.json();
        console.log("Fetched Users:", result);
        setUsers(result?.data?.users || []);
        setTotalRecords(result?.data?.total || 0);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

  const fetchRoles = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_FORE_APILINK}/admin/get-all-roles`);
        const result = await response.json();
        console.log("Fetched Roles:", result);
        setRoles(result?.roles || []);
      } catch (error) {
        console.error(error);
      }
    };

  const fetchHubs = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_FORE_BRICKBUCKET}hub/get-all-hubs`);
        const result = await response.json();
        console.log("Fetched Hubs:", result);
        setHubs(result?.data || []);
      } catch (error) {
        console.error(error);
      }
    };

  const filteredUsers = useMemo(() => {
    const normalizedSearch = search.toLowerCase().trim();

    return (users || []).filter((user) => {
      const matchesSearch = !normalizedSearch || [
        user.username,
        user.firstName,
        user.lastName,
        user.email,
        user.mobileNo,
      ].some((value) => String(value || "").toLowerCase().includes(normalizedSearch));

      const matchesRole = !roleFilter || String(user.roleId) === String(roleFilter);
      const matchesStatus = statusFilter === "all" || String(user.is_active) === statusFilter;

      return matchesSearch && matchesRole && matchesStatus;
    });
  }, [users, search, roleFilter, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / pageSize));
  const pagedUsers = filteredUsers.slice((page - 1) * pageSize, page * pageSize);

  const openCreateModal = () => {
    setSelectedUser(null);
    setShowModal(true);
  };

  const openEditModal = (user) => {
    setSelectedUser(user);
    setShowModal(true);
  };

  const openViewDrawer = (user) => {
    setSelectedUser(user);
    setShowDrawer(true);
  };

  const openPermissionDrawer = async (user) => {
      try {
        setSelectedUser(user);
        const response = await fetch(`${process.env.REACT_APP_FORE_BRICKBUCKET}role/get-role-details?roleId=${user.roleId}`);
        const result = await response.json();
        setPermissions(result?.data?.privilages || []);
        setShowPermissionDrawer(true);
      } catch (error) {
        console.error(error);
      }
    };

  return (
    <div className="p-6">
      <div className="mb-5 flex items-center justify-between">
        <div>
          <h1 className="text-lg font-bold">
            User Management
          </h1>
          <p className="text-sm text-gray-500">
            Manage users, roles and permissions
          </p>
        </div>
        <button onClick={openCreateModal} className="rounded-lg bg-[#F7525A] px-5 py-2 text-white">Add User</button>
      </div>

      <UserFilters
        search={search}
        setSearch={setSearch}
        roleFilter={roleFilter}
        setRoleFilter={setRoleFilter}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        roles={roles}
      />

      <div className="mt-4">
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          totalItems={filteredUsers.length}
          itemsPerPage={pageSize}
          showInfo={true}
          hideControls={true}
          itemName="users"
        />
      </div>

      <UserTable
        users={pagedUsers}
        roles={roles}
        hubs={hubs}
        loading={loading}
        openEditModal={openEditModal}
        openViewDrawer={openViewDrawer}
        openPermissionDrawer={openPermissionDrawer}
        setSelectedUser={setSelectedUser}
        setShowResetModal={setShowResetModal}
      />

      <div className="mt-4">
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          totalItems={filteredUsers.length}
          itemsPerPage={pageSize}
          onPageChange={setPage}
          onPreviousPage={() => setPage((prev) => Math.max(1, prev - 1))}
          onNextPage={() => setPage((prev) => Math.min(totalPages, prev + 1))}
          showInfo={false}
          itemName="users"
        />
      </div>

      {showModal && (
        <UserModal
          user={selectedUser}
          roles={roles}
          hubs={hubs}
          onClose={() => setShowModal(false)}
          refreshUsers={fetchUsers}
        />
      )}

      {showDrawer && (
        <ViewUserDrawer
          user={selectedUser}
          hubs={hubs}
          roles={roles}
          onClose={() => setShowDrawer(false)}
        />
      )}

      {showPermissionDrawer && (
        <PermissionDrawer
          permissions={permissions}
          onClose={() =>
            setShowPermissionDrawer(false)
          }
        />
      )}

      {showResetModal && (
        <ResetPasswordModal
          user={selectedUser}
          onClose={() =>
            setShowResetModal(false)
          }
        />
      )}
    </div>
  );
};

export default UserManagement;